import {useLocation} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import {DetalleQuickviewPublic} from "../components/claim";
import {
    Button,
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
    Chip,
    Progress,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    useDisclosure,
    Modal,
    ModalContent,
    ModalHeader, ModalBody, ModalFooter, Alert, Input, Card, CardBody, Divider, Spacer, Image, Skeleton
} from "@heroui/react";
import {CgDarkMode} from "react-icons/cg";
import {Icon} from "@iconify/react";
import {RSKLogo} from "../components/NavBar";
import {motion} from "framer-motion";
import {toZonedTime} from "date-fns-tz";
import {format} from "date-fns";
import {fetchClaimsDetailDataCarrier} from "../services/carrierService";
import {useTheme} from "@heroui/use-theme";

const ClaimDetailPublic = ({code}) => {
    const location = useLocation(); // PARA OBTENER LA LOCALIZACION ASI COMO LOS SEARCH PARAMS
    const [loading, setLoading] = useState(true);
    const [reclamoData, setReclamoData] = useState([]);
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [rskRef, setRskRef] = useState(null);
    const [caseTitle, setCaseTitle] = useState("");
    const prevSearch = useRef(null); //evitar re
    const { theme, setTheme } = useTheme()
    const [darkMode, setDarkMode] = useState(() => {
        const storedPreference = localStorage.getItem('darkMode');
        return storedPreference ? JSON.parse(storedPreference) : false;
    });
    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        setTheme(darkMode ? 'light' : 'dark'); // Toggle theme based on darkMode state
    };

    useEffect(() => {
        // Comparar con el valor anterior
        if (prevSearch.current === location.search) return;

        const fetchData = async () => {
            try {
                const data = await fetchClaimsDetailDataCarrier(code);
                console.log("reclamodata", data);
                setReclamoData(data);
            } catch (err) {
                console.error("Error al cargar los detalles del reclamo:", err);

                // Manejar específicamente el error 403
                if (err.response && err.response.status === 403) {
                    setReclamoData({ error: "403" }); // Indicador de error para 403
                } else {
                    setReclamoData({ error: "general" }); // Para otros errores
                }

            } finally {
                setLoading(false);
            }
        };

        fetchData();
        prevSearch.current = location.search; // Actualizar el valor anterior
    }, [location.search]);


    // Si hay mas de uno podria considerarse un error
    const uniqueContrapartes = Array.from(
        new Set(reclamoData.reclamos?.map((item) => item.contraparte))
    ).filter(Boolean); // .filter(Boolean) elimina valores nulos o undefined

    const headerFields = [
        {
            icon: "lucide:ship",
            label: "Carrier",
            value: uniqueContrapartes.length > 0
                ? uniqueContrapartes.join(", ") // Une los valores como texto separados por comas
                : "N/A" // Si no hay valores, mostrar 'N/A'
        },
        {
            icon: "lucide:calendar",
            label: "Claim Date",
            value: reclamoData.reclamo?.created
                ? format(toZonedTime(new Date(reclamoData.reclamo?.created), "UTC"), "dd-MM-yyyy")
                : "N/A",
        },
        {
            icon: "lucide:dollar-sign",
            label: "Total Claim Amount",
            value: new Intl.NumberFormat("es-CL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(
                reclamoData.reclamos?.reduce((total, claim) => total + parseFloat(claim.reclamo_usd || 0), 0)
            ),
        },
        {
            icon: "lucide:hash",
            label: "Unique Code",
            value: reclamoData.reclamo?.unique_code || "N/A",
        },
    ];

    const handleQuickViewClick = (rsk_ref) => {
        setRskRef(rsk_ref);
        setCaseTitle(""); // Resetear el título al abrir
        onOpen();
    };

    const handleCloseModal = () => {
        setCaseTitle(""); // Resetear el título al cerrar
        onClose();
    };

    const getHeaderTitle = () => {
        if (caseTitle) {
            return `Quickview: ${caseTitle}`;
        }
        return "Quickview";
    };

    return (
        <>
            <Navbar className="mb-2">
                <NavbarBrand>
                    <div className="flex gap-1 justify-center items-center" >
                        <RSKLogo />
                        <Image
                            src= {process.env.PUBLIC_URL + "/PoweredBy.png"}
                            className="max-w-[55px] max-h-[30px] object-contain"
                            width={55}
                            height={30}
                            alt="AWS Logo"
                        />
                    </div>
                </NavbarBrand>

                <NavbarContent className="hidden sm:flex gap-4" justify="center">
                    {uniqueContrapartes.map((contraparte) => (
                        <NavbarItem key={contraparte}>
                            <Chip>{contraparte}</Chip>
                        </NavbarItem>
                    ))}
                </NavbarContent>

                <NavbarContent justify="end">
                    <NavbarItem>
                        <Button
                            isIconOnly
                            onPress={toggleDarkMode}
                            aria-label="Toggle Theme"
                            className="transition-all duration-300 hover:scale-110"
                            style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
                        >
                            <Icon
                                icon={theme === "light" ? "solar:moon-stars-bold-duotone" : "solar:sun-2-bold-duotone"}
                                width={28}
                                height={28}
                                className={theme === "light" ? "text-blue-700" : "text-yellow-400"}
                            />
                        </Button>
                    </NavbarItem>
                </NavbarContent>
            </Navbar>

            {/* Loader mientras carga */}
            {loading && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Progress isIndeterminate aria-label="Cargando..." className="max-w-full" size="sm" />
                </motion.div>
            )}

            {/* Mostrar mensaje para error 403 */}
            {!loading && reclamoData.error === "403" && (
                <div className="flex items-center justify-center min-h-screen">
                    <Alert
                        className="mx-auto w-full max-w-3xl"
                        color="danger"
                        title="Error"
                        description="El link ya no está disponible, comuníquese con RSK Group para solicitar la reactivación."
                    />
                </div>
            )}

            {/* Mostrar mensaje para otros errores */}
            {!loading && reclamoData.error === "general" && (
                <div className="flex items-center justify-center min-h-screen">
                    <Alert
                        className="mx-auto w-full max-w-3xl"
                        color="danger"
                        title="Error"
                        description="Ocurrió un error al cargar los detalles. Por favor, intente nuevamente más tarde."
                    />
                </div>
            )}

            {/* Tabla con HeroUI */}
            {!loading && !reclamoData.error && reclamoData.reclamos?.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, scale: 1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                >

                    <div className="mx-auto mt-4 w-full max-w-3xl">
                        <Card className="w-full">
                            <CardBody>
                                <h2 className="text-2xl font-bold">Claim Details</h2>
                                <Divider className="my-4" />
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {headerFields.map((field, index) => (
                                        <div key={index} className="w-full">
                                            <Input
                                                isReadOnly
                                                label={field.label}
                                                value={field.value}
                                                startContent={
                                                    <Icon
                                                        icon={field.icon}
                                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                                    />
                                                }
                                                classNames={{
                                                    label: "text-sm font-medium",
                                                    input: "text-sm"
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    <Table aria-label="Detalles de reclamos" className="mx-auto mt-4 w-full max-w-3xl" isStriped>
                        <TableHeader>
                            <TableColumn hidden>ID</TableColumn>
                            <TableColumn>RSK Ref.</TableColumn>
                            <TableColumn>Exporter</TableColumn>
                            <TableColumn>BL/AWB</TableColumn>
                            <TableColumn>Claim Amount</TableColumn>
                            <TableColumn>Options</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {reclamoData.reclamos?.map((item, index) => (
                                <TableRow
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <TableCell hidden>{item.id}</TableCell>
                                    <TableCell>{item.rsk_ref || "Sin RSK Ref."}</TableCell>
                                    <TableCell>{item.embarcador || "Sin Embarcador"}</TableCell>
                                    <TableCell>{item.bl_awb || "Sin BL/AWB"}</TableCell>
                                    <TableCell>
                                        ${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.reclamo_usd || 0)}
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            color="success"
                                            size="sm"
                                            onClick={() => {
                                                handleQuickViewClick(item.rsk_ref)
                                            }}
                                            role="button"
                                            className="mb-1 mr-1"
                                        >
                                            QuickView
                                        </Chip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </motion.div>
            )}

            <Modal isOpen={isOpen} size="full" onClose={handleCloseModal} scrollBehavior="inside" isDismissable={false}
                   isKeyboardDismissDisabled={true} >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                {!caseTitle ? (
                                    <div className="flex items-center gap-2">
                                        <span>Quickview:</span>
                                        <Skeleton className="h-6 w-64 rounded-lg" />
                                    </div>
                                ) : (
                                    getHeaderTitle()
                                )}
                            </ModalHeader>
                            <ModalBody>
                                <DetalleQuickviewPublic 
                                    rskRef={rskRef} 
                                    code={code}
                                    onTitleLoaded={setCaseTitle}
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    color="danger"
                                    variant="light"
                                    onPress={() => {
                                        handleCloseModal();
                                    }}
                                >
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}

export default ClaimDetailPublic